import { ProfileCurve, ProfilePoint } from '../types/missionPlanner';
import { REGULATORY_CEILING_M } from './geofence';

/** Which profile a curve edits. */
export type ProfileAxis = 'altitude' | 'speed';

export interface Capability {
  min: number;
  max: number;
  unit: string;
  label: string;
}

/**
 * Drone capability envelope. Out-of-range edits are clamped and flagged loud
 * (safety-critical: fail safe, not silent). 20 m/s is a conservative small-quad
 * cruise cap.
 *
 * The altitude ceiling was already 120 m here, written as its own literal,
 * while the backend refused anything over 100 m: the planner cleared heights
 * the server answered 400 to, and had done since the two were written. #541
 * made the limit one value both sides read, and this now takes it from there.
 */
export const CAPABILITY: Record<ProfileAxis, Capability> = {
  altitude: { min: 0, max: REGULATORY_CEILING_M, unit: 'm', label: 'Altitude' },
  speed: { min: 0, max: 20, unit: 'm/s', label: 'Speed' },
};

/** True when value is within the axis capability envelope. */
export const inRange = (axis: ProfileAxis, value: number): boolean =>
  Number.isFinite(value) &&
  value >= CAPABILITY[axis].min &&
  value <= CAPABILITY[axis].max;

/** Clamp a value to the axis capability envelope. */
export const clampValue = (axis: ProfileAxis, value: number): number =>
  Math.min(CAPABILITY[axis].max, Math.max(CAPABILITY[axis].min, value));

const sortedPoints = (points: ProfilePoint[]): ProfilePoint[] =>
  [...points].sort((a, b) => a.distanceKm - b.distanceKm);

/**
 * Shape-preserving one-sided tangent for a PCHIP endpoint (Fritsch–Carlson,
 * per Moler's `pchip`). Clamps so the end interval never overshoots the
 * bracketing point values. `h1`/`d1` are the end interval; `h2`/`d2` the next.
 */
const pchipEnd = (h1: number, h2: number, d1: number, d2: number): number => {
  let d = ((2 * h1 + h2) * d1 - h1 * d2) / (h1 + h2);
  if (Math.sign(d) !== Math.sign(d1)) {
    d = 0;
  } else if (Math.sign(d1) !== Math.sign(d2) && Math.abs(d) > 3 * Math.abs(d1)) {
    d = 3 * d1;
  }
  return d;
};

/**
 * Tangents at each knot for a monotone cubic (PCHIP / Fritsch–Carlson)
 * interpolant through (xs, ys). Unlike a natural cubic spline these never
 * overshoot past the bracketing point values — essential here: route control
 * points sit ~tens of metres (≈0.02 km) apart, and an overshooting spline
 * produced huge out-of-range values that blew out the chart axes (issue #340).
 */
const pchipSlopes = (xs: number[], ys: number[]): number[] => {
  const n = xs.length;
  const h = new Array(n - 1);
  const delta = new Array(n - 1);
  for (let i = 0; i < n - 1; i++) {
    h[i] = xs[i + 1] - xs[i];
    delta[i] = (ys[i + 1] - ys[i]) / h[i];
  }
  const d = new Array(n).fill(0);
  if (n === 2) {
    d[0] = delta[0];
    d[1] = delta[0];
    return d;
  }
  // Interior knots: weighted harmonic mean of neighbouring secants; a sign
  // change (or a flat segment) pins the tangent to zero — a local extremum sits
  // exactly on a control point, never between them, so the curve stays bracketed.
  for (let i = 1; i < n - 1; i++) {
    if (delta[i - 1] * delta[i] <= 0) {
      d[i] = 0;
    } else {
      const w1 = 2 * h[i] + h[i - 1];
      const w2 = h[i] + 2 * h[i - 1];
      d[i] = (w1 + w2) / (w1 / delta[i - 1] + w2 / delta[i]);
    }
  }
  d[0] = pchipEnd(h[0], h[1], delta[0], delta[1]);
  d[n - 1] = pchipEnd(h[n - 2], h[n - 3], delta[n - 2], delta[n - 3]);
  return d;
};

/** Cubic Hermite evaluation of the PCHIP interpolant at `x`. */
const evalPchip = (
  xs: number[],
  ys: number[],
  d: number[],
  x: number
): number => {
  let i = 0;
  while (i < xs.length - 2 && x > xs[i + 1]) i++;
  const h = xs[i + 1] - xs[i];
  const t = (x - xs[i]) / h;
  const t2 = t * t;
  const t3 = t2 * t;
  const h00 = 2 * t3 - 3 * t2 + 1;
  const h10 = t3 - 2 * t2 + t;
  const h01 = -2 * t3 + 3 * t2;
  const h11 = t3 - t2;
  return (
    h00 * ys[i] + h10 * h * d[i] + h01 * ys[i + 1] + h11 * h * d[i + 1]
  );
};

/**
 * Value of a curve at `distanceKm`. With no control points it returns
 * `fallback` (the global default); otherwise it interpolates per the preset,
 * holding the endpoint value outside the control-point range.
 */
export const sampleCurve = (
  curve: ProfileCurve,
  distanceKm: number,
  fallback: number
): number => {
  const pts = sortedPoints(curve.points);
  if (pts.length === 0) return fallback;
  if (pts.length === 1) return pts[0].value;
  if (distanceKm <= pts[0].distanceKm) return pts[0].value;
  if (distanceKm >= pts[pts.length - 1].distanceKm) {
    return pts[pts.length - 1].value;
  }

  if (curve.preset === 'stepped') {
    let v = pts[0].value;
    for (const p of pts) {
      if (p.distanceKm <= distanceKm) v = p.value;
      else break;
    }
    return v;
  }

  if (curve.preset === 'smooth') {
    const xs = pts.map((p) => p.distanceKm);
    const ys = pts.map((p) => p.value);
    return evalPchip(xs, ys, pchipSlopes(xs, ys), distanceKm);
  }

  // linear
  for (let i = 0; i < pts.length - 1; i++) {
    const a = pts[i];
    const b = pts[i + 1];
    if (distanceKm >= a.distanceKm && distanceKm <= b.distanceKm) {
      const t = (distanceKm - a.distanceKm) / (b.distanceKm - a.distanceKm);
      return a.value + t * (b.value - a.value);
    }
  }
  return pts[pts.length - 1].value;
};

/** Sampled series [{distanceKm, value}] across [0, xMaxKm] for charting. */
export const curveSeries = (
  curve: ProfileCurve,
  xMaxKm: number,
  fallback: number,
  steps = 64
): Array<{ distanceKm: number; value: number }> => {
  const span = xMaxKm > 0 ? xMaxKm : 1;
  return Array.from({ length: steps + 1 }, (_, i) => {
    const distanceKm = (i / steps) * span;
    return { distanceKm, value: sampleCurve(curve, distanceKm, fallback) };
  });
};
